import {useEffect, useState} from 'react';
import {useGetPlayoffScheduleMutation} from 'app/api';
import {Round} from 'types/dto';

interface UsePlayoffScheduleProps {
  eventId: number;
  leagues?: Array<{ id: number; name: string }>;
}

interface UsePlayoffScheduleReturn {
  selectedLeague: number | undefined;
  setSelectedLeague: (leagueId: number) => void;
  playoffSchedules: { [key: number]: Round[] };
  showPlayoffSchedule: Round[];
  refreshSchedule: (leagueId: number) => Promise<void>;
  isDoubleElimination: boolean;
}

export const usePlayoffSchedule = ({
                                     eventId,
                                     leagues,
                                   }: UsePlayoffScheduleProps): UsePlayoffScheduleReturn => {
  const [selectedLeague, setSelectedLeague] = useState<number | undefined>();
  const [playoffSchedules, setPlayoffSchedules] = useState<{
    [key: number]: Round[];
  }>({});
  const [showPlayoffSchedule, setShowPlayoffSchedule] = useState<Round[]>([]);

  const [getPlayoffSchedule] = useGetPlayoffScheduleMutation();

  useEffect(() => {
    if (!selectedLeague && leagues && leagues.length > 0) {
      setSelectedLeague(leagues[0].id);
    }
  }, [leagues, selectedLeague]);

  useEffect(() => {
    if (selectedLeague && eventId) {
      getPlayoffSchedule({
        eventId,
        leagueId: selectedLeague,
      })
        .unwrap()
        .then((schedule) => {
          setPlayoffSchedules((prev) => ({
            ...prev,
            [selectedLeague]: schedule,
          }));
        })
        .catch((error) => {
          console.error('Failed to fetch playoff schedule:', error);
        });
    }
  }, [selectedLeague, eventId, getPlayoffSchedule]);

  useEffect(() => {
    if (selectedLeague) {
      setShowPlayoffSchedule(playoffSchedules[selectedLeague] ?? []);
    }
  }, [selectedLeague, playoffSchedules]);

  const refreshSchedule = async (leagueId: number): Promise<void> => {
    try {
      const schedule = await getPlayoffSchedule({
        eventId,
        leagueId,
      }).unwrap();

      setPlayoffSchedules((prev) => ({
        ...prev,
        [leagueId]: schedule,
      }));
    } catch (error) {
      console.error('Failed to refresh playoff schedule:', error);
      throw error;
    }
  };

  const isDoubleElimination = showPlayoffSchedule.some(
    (round, i, arr) => arr.find((round1) => round1.id === round.id) !== round
  );

  return {
    selectedLeague,
    setSelectedLeague,
    playoffSchedules,
    showPlayoffSchedule,
    refreshSchedule,
    isDoubleElimination,
  };
};
